import type { JSX } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { postBulkUpdatesForUpdateReady } from '@/api/fota'
import type { FOTAJobListView } from '@/pages/fota/fota'
import FotaCheckStep from '@/pages/fota/FotaCheckStep'

type Props = {
  setActiveStep: (step: number) => void
  selectedRows: number[]
  setSelectedRows: (selectedRows: number[]) => void
}

const SendToUpdateQueuedStep = ({
  setActiveStep,
  selectedRows,
  setSelectedRows,
}: Props): JSX.Element => {
  const [t] = useTranslation(['di', 'app', 'common'])
  const [isLoading, setIsLoading] = useState(true)
  const [result, setResult] = useState<FOTAJobListView[]>([])
  const [error, setError] = useState<string | null>(null)

  const handleBackClick = () => {
    setResult([])
    setSelectedRows([])
    setActiveStep(0)
  }

  useEffect(() => {
    let isMounted = true
    setIsLoading(true)
    setError(null)

    if (selectedRows.length === 0) {
      setIsLoading(false)
      return
    }

    postBulkUpdatesForUpdateReady(selectedRows)
      .then((data) => {
        if (isMounted) {
          setResult(data.jobs ?? [])
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message ?? 'Failed to load data')
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [selectedRows])

  const checkPassedCount = useMemo(
    () => result.filter((e) => e.check_passed).length,
    [result],
  )

  const checkFailedCount = useMemo(
    () => result.length - checkPassedCount,
    [result, checkPassedCount],
  )

  // Custom data grid props specific to this component
  const dataGridProps = {
    height: 400,
  }

  return (
    <FotaCheckStep
      isLoading={isLoading}
      result={result}
      error={error}
      checkPassedCount={checkPassedCount}
      checkFailedCount={checkFailedCount}
      handleBackClick={handleBackClick}
      t={t}
      showTypeColumn={false}
      dataGridProps={dataGridProps}
    />
  )
}

export default React.memo(SendToUpdateQueuedStep)
